import React, { PureComponent } from "react";
import { Table, Spin, Button, Tag } from "antd";
import { useCubeQuery } from "@cubejs-client/react";
import { withRouter } from "react-router-dom";
import moment from "moment";

// Fetch ERP base URL from config endpoint, fallback to UAT
let ERP_BASE_URL = "https://erp.tanksoftpro.com/erp_uat/";
try {
  const xhr = new XMLHttpRequest();
  xhr.open("GET", "/auth/config", false);
  xhr.send();
  if (xhr.status === 200) {
    const config = JSON.parse(xhr.responseText);
    if (config.erpBaseUrl) {
      ERP_BASE_URL = config.erpBaseUrl.endsWith("/") ? config.erpBaseUrl : config.erpBaseUrl + "/";
    }
  }
} catch (e) {
  // use fallback
}

const SO_TAB_ID = "186";
const WO_TAB_ID = "AB955F8DD46E4F308199E14B13AECAE5";

const formatCurrency = (val) => {
  if (val === null || val === undefined || isNaN(Number(val))) return "$0";
  return "$" + Number(val).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const formatDate = (val) => {
  if (!val) return "";
  return moment(val).format("MM/DD/YYYY");
};

const erpLink = (tabId, recordId, label) => {
  if (!recordId) return label || "";
  const url = `${ERP_BASE_URL}?tabId=${tabId}&recordId=${recordId}`;
  return (
    <a href={url} target="_blank" rel="noopener noreferrer" style={{ color: "#1890ff" }}>
      {label || recordId}
    </a>
  );
};

// TSP-116: Link WO document number to Mobile Work Order (yardcheck) instead of back office
const woMobileLink = (woDocNo) => {
  if (!woDocNo) return "";
  const url = `${ERP_BASE_URL}web/com.agilityerp.openbravo.mobile.quala.yardcheck/#wo-workorder&documentno=${woDocNo}`;
  return (
    <a href={url} target="_blank" rel="noopener noreferrer" style={{ color: "#1890ff" }}>
      {woDocNo}
    </a>
  );
};

const statusTag = (status) => {
  const colorMap = {
    Open: "blue",
    Scheduled: "gold",
    "In Process": "green",
    Stop: "red",
    Completed: "purple",
    Closed: "default",
  };
  return <Tag color={colorMap[status] || "default"}>{status || "N/A"}</Tag>;
};

// Row warning color logic
const getRowWarningStyle = (row, reportType) => {
  const yellowKey = getWarningKey(reportType, "yellow");
  const redKey = getWarningKey(reportType, "red");
  const brownKey = getWarningKey(reportType, "brown");

  if (row[redKey] === "Y") return { background: "#fee2e2" }; // light red
  if (row[yellowKey] === "Y") return { background: "#fef9c3" }; // light yellow
  if (row[brownKey] === "Y") return { background: "#fde68a" }; // light amber/brown
  return {};
};

const getWarningKey = (reportType, color) => {
  const prefix = reportType === "delayed" ? "DelayedSOrder" : "PendingWorkOrder";
  return `${prefix}.${color}_warning`;
};

// Report-specific configs
const REPORT_CONFIGS = {
  pending: {
    title: "Pending Orders to Work",
    color: "#2563eb",
    query: {
      measures: ["PendingWorkOrder.pending_count"],
      dimensions: [
        "PendingWorkOrder.documentno",
        "PendingWorkOrder.sdocumentno",
        "CBpartner.name",
        "PendingWorkOrder.sop_requested_date",
        "PendingWorkOrder.scheduled_bay",
        "ContainerPlan.contmng_plan_name",
        "PendingWorkOrder.grandtotal",
        "PendingWorkOrder.c_order_id",
        "PendingWorkOrder.contmng_workorder_id",
        "PendingWorkOrder.wo_status",
        "PendingWorkOrder.containerId",
        "PendingWorkOrder.commonname",
        "PendingWorkOrder.disposal_code",
        "PendingWorkOrder.yellow_warning",
        "PendingWorkOrder.red_warning",
        "PendingWorkOrder.brown_warning",
        "PendingWorkOrder.wo_pdf_path",
      ],
    },
    columns: (rows) => [
      {
        title: "WO Document",
        dataIndex: "PendingWorkOrder.documentno",
        key: "wo_doc",
        render: (text) => woMobileLink(text),
        sorter: (a, b) => (a["PendingWorkOrder.documentno"] || "").localeCompare(b["PendingWorkOrder.documentno"] || ""),
      },
      {
        title: "Customer",
        dataIndex: "CBpartner.name",
        key: "customer",
        sorter: (a, b) => (a["CBpartner.name"] || "").localeCompare(b["CBpartner.name"] || ""),
      },
      {
        title: "Container",
        dataIndex: "PendingWorkOrder.containerId",
        key: "container",
      },
      {
        title: "Content",
        dataIndex: "PendingWorkOrder.commonname",
        key: "content",
      },
      {
        title: "Heel Disposal",
        dataIndex: "PendingWorkOrder.disposal_code",
        key: "disposal",
      },
      {
        title: "Bay",
        dataIndex: "PendingWorkOrder.scheduled_bay",
        key: "bay",
        sorter: (a, b) => (a["PendingWorkOrder.scheduled_bay"] || "").localeCompare(b["PendingWorkOrder.scheduled_bay"] || ""),
      },
      {
        title: "Service Plan",
        dataIndex: "ContainerPlan.contmng_plan_name",
        key: "plan",
      },
      {
        title: "Requested Date",
        dataIndex: "PendingWorkOrder.sop_requested_date",
        key: "req_date",
        render: (text) => formatDate(text),
        sorter: (a, b) => new Date(a["PendingWorkOrder.sop_requested_date"] || 0) - new Date(b["PendingWorkOrder.sop_requested_date"] || 0),
      },
      {
        title: "Status",
        dataIndex: "PendingWorkOrder.wo_status",
        key: "status",
        render: (text) => statusTag(text),
        filters: [...new Set(rows.map((r) => r["PendingWorkOrder.wo_status"]))].filter(Boolean).map((s) => ({ text: s, value: s })),
        onFilter: (value, record) => record["PendingWorkOrder.wo_status"] === value,
      },
      {
        title: "Grand Total",
        dataIndex: "PendingWorkOrder.grandtotal",
        key: "total",
        render: (text) => formatCurrency(text),
        sorter: (a, b) => Number(a["PendingWorkOrder.grandtotal"] || 0) - Number(b["PendingWorkOrder.grandtotal"] || 0),
        align: "right",
      },
    ],
    categoryFilterDimension: {
      bay: "PendingWorkOrder.scheduled_bay",
      customer: "CBpartner.name",
      product: "Product.name",
    },
    hasWarnings: true,
  },
  delayed: {
    title: "Delayed > 48 Hours",
    color: "#dc2626",
    query: {
      measures: ["DelayedSOrder.delayed_gt_48_count"],
      dimensions: [
        "DelayedSOrder.documentno",
        "CBpartner.name",
        "DelayedSOrder.sop_requested_date",
        "DelayedSOrder.scheduled_bay",
        "ContainerPlan.contmng_plan_name",
        "DelayedSOrder.grandtotal",
        "DelayedSOrder.corder_id",
        "DelayedSOrder.wo_status",
        "DelayedSOrder.wo_documentno",
        "DelayedSOrder.contmng_workorder_id",
        "DelayedSOrder.containerId",
        "DelayedSOrder.commonname",
        "DelayedSOrder.disposal_code",
        "DelayedSOrder.yellow_warning",
        "DelayedSOrder.red_warning",
        "DelayedSOrder.brown_warning",
        "DelayedSOrder.wo_pdf_path",
      ],
    },
    columns: (rows) => [
      {
        title: "WO Document",
        dataIndex: "DelayedSOrder.wo_documentno",
        key: "wo_doc",
        render: (text) => woMobileLink(text),
        sorter: (a, b) => (a["DelayedSOrder.wo_documentno"] || "").localeCompare(b["DelayedSOrder.wo_documentno"] || ""),
      },
      {
        title: "Customer",
        dataIndex: "CBpartner.name",
        key: "customer",
        sorter: (a, b) => (a["CBpartner.name"] || "").localeCompare(b["CBpartner.name"] || ""),
      },
      {
        title: "Container",
        dataIndex: "DelayedSOrder.containerId",
        key: "container",
      },
      {
        title: "Content",
        dataIndex: "DelayedSOrder.commonname",
        key: "content",
      },
      {
        title: "Heel Disposal",
        dataIndex: "DelayedSOrder.disposal_code",
        key: "disposal",
      },
      {
        title: "Bay",
        dataIndex: "DelayedSOrder.scheduled_bay",
        key: "bay",
      },
      {
        title: "Service Plan",
        dataIndex: "ContainerPlan.contmng_plan_name",
        key: "plan",
      },
      {
        title: "Requested Date",
        dataIndex: "DelayedSOrder.sop_requested_date",
        key: "req_date",
        render: (text) => formatDate(text),
        sorter: (a, b) => new Date(a["DelayedSOrder.sop_requested_date"] || 0) - new Date(b["DelayedSOrder.sop_requested_date"] || 0),
      },
      {
        title: "Status",
        dataIndex: "DelayedSOrder.wo_status",
        key: "status",
        render: (text) => statusTag(text),
        filters: [...new Set(rows.map((r) => r["DelayedSOrder.wo_status"]))].filter(Boolean).map((s) => ({ text: s, value: s })),
        onFilter: (value, record) => record["DelayedSOrder.wo_status"] === value,
      },
      {
        title: "Grand Total",
        dataIndex: "DelayedSOrder.grandtotal",
        key: "total",
        render: (text) => formatCurrency(text),
        sorter: (a, b) => Number(a["DelayedSOrder.grandtotal"] || 0) - Number(b["DelayedSOrder.grandtotal"] || 0),
        align: "right",
      },
    ],
    categoryFilterDimension: {
      bay: "DelayedSOrder.scheduled_bay",
      customer: "CBpartner.name",
      product: "Product.name",
    },
    hasWarnings: true,
  },
  missingpo: {
    title: "Missing POs",
    color: "#ea580c",
    query: {
      measures: ["MissedSOrder.missing_po_count"],
      dimensions: [
        "MissedSOrder.documentno",
        "MissedSOrder.wo_documentno",
        "MissedSOrder.bpname",
        "MissedSOrder.container_type_name",
        "MissedSOrder.sop_requested_date",
        "MissedSOrder.po_reference",
        "MissedSOrder.grandtotal",
        "MissedSOrder.corder_id",
        "MissedSOrder.contmng_workorder_id",
        "MissedSOrder.wo_status",
        "MissedSOrder.wo_pdf_path",
      ],
    },
    columns: (rows) => [
      {
        title: "WO Document",
        dataIndex: "MissedSOrder.wo_documentno",
        key: "wo_doc",
        render: (text) => woMobileLink(text),
        sorter: (a, b) => (a["MissedSOrder.wo_documentno"] || "").localeCompare(b["MissedSOrder.wo_documentno"] || ""),
      },
      {
        title: "Customer",
        dataIndex: "MissedSOrder.bpname",
        key: "customer",
        sorter: (a, b) => (a["MissedSOrder.bpname"] || "").localeCompare(b["MissedSOrder.bpname"] || ""),
      },
      {
        title: "Container Type",
        dataIndex: "MissedSOrder.container_type_name",
        key: "container_type",
      },
      {
        title: "Requested Date",
        dataIndex: "MissedSOrder.sop_requested_date",
        key: "req_date",
        render: (text) => formatDate(text),
        sorter: (a, b) => new Date(a["MissedSOrder.sop_requested_date"] || 0) - new Date(b["MissedSOrder.sop_requested_date"] || 0),
      },
      {
        title: "PO Reference",
        dataIndex: "MissedSOrder.po_reference",
        key: "po_ref",
        render: (text) => text || <span style={{ color: "#ef4444", fontStyle: "italic" }}>Missing</span>,
      },
      {
        title: "Status",
        dataIndex: "MissedSOrder.wo_status",
        key: "status",
        render: (text) => statusTag(text),
        filters: [...new Set(rows.map((r) => r["MissedSOrder.wo_status"]))].filter(Boolean).map((s) => ({ text: s, value: s })),
        onFilter: (value, record) => record["MissedSOrder.wo_status"] === value,
      },
      {
        title: "Grand Total",
        dataIndex: "MissedSOrder.grandtotal",
        key: "total",
        render: (text) => formatCurrency(text),
        sorter: (a, b) => Number(a["MissedSOrder.grandtotal"] || 0) - Number(b["MissedSOrder.grandtotal"] || 0),
        align: "right",
      },
    ],
    categoryFilterDimension: {
      customer: "MissedSOrder.bpname",
      product: "Product.name",
    },
    hasWarnings: false,
  },
  pendingbill: {
    title: "Pending to Bill",
    color: "#8b5cf6",
    query: {
      measures: ["PendingWorkOrderBill.pending_count"],
      dimensions: [
        "PendingWorkOrderBill.sdocumentno",
        "CBpartner.name",
        "PendingWorkOrderBill.documentno",
        "PendingWorkOrderBill.wo_status",
        "PendingWorkOrderBill.enddate",
        "PendingWorkOrderBill.agediff",
        "PendingWorkOrderBill.grandtotal",
        "PendingWorkOrderBill.payment_method",
        "PendingWorkOrderBill.poreference",
        "PendingWorkOrderBill.c_order_id",
        "PendingWorkOrderBill.cont_workorder_id",
        "PendingWorkOrderBill.wo_pdf_path",
      ],
    },
    columns: (rows) => [
      {
        title: "WO Document",
        dataIndex: "PendingWorkOrderBill.documentno",
        key: "wo_doc",
        render: (text) => woMobileLink(text),
        sorter: (a, b) => (a["PendingWorkOrderBill.documentno"] || "").localeCompare(b["PendingWorkOrderBill.documentno"] || ""),
      },
      {
        title: "Customer",
        dataIndex: "CBpartner.name",
        key: "customer",
        sorter: (a, b) => (a["CBpartner.name"] || "").localeCompare(b["CBpartner.name"] || ""),
      },
      {
        title: "WO Status",
        dataIndex: "PendingWorkOrderBill.wo_status",
        key: "status",
        render: (text) => statusTag(text),
        filters: [...new Set(rows.map((r) => r["PendingWorkOrderBill.wo_status"]))].filter(Boolean).map((s) => ({ text: s, value: s })),
        onFilter: (value, record) => record["PendingWorkOrderBill.wo_status"] === value,
      },
      {
        title: "End Date",
        dataIndex: "PendingWorkOrderBill.enddate",
        key: "enddate",
        render: (text) => formatDate(text),
        sorter: (a, b) => new Date(a["PendingWorkOrderBill.enddate"] || 0) - new Date(b["PendingWorkOrderBill.enddate"] || 0),
      },
      {
        title: "Age",
        dataIndex: "PendingWorkOrderBill.agediff",
        key: "age",
        render: (text) => {
          const colorMap = { "<2": "green", "2-6": "gold", "7-14": "orange", "15-31": "red", ">31": "#991b1b" };
          return <Tag color={colorMap[text] || "default"}>{text || "N/A"} days</Tag>;
        },
        filters: ["<2", "2-6", "7-14", "15-31", ">31"].map((s) => ({ text: s + " days", value: s })),
        onFilter: (value, record) => record["PendingWorkOrderBill.agediff"] === value,
      },
      {
        title: "Grand Total",
        dataIndex: "PendingWorkOrderBill.grandtotal",
        key: "total",
        render: (text) => formatCurrency(text),
        sorter: (a, b) => Number(a["PendingWorkOrderBill.grandtotal"] || 0) - Number(b["PendingWorkOrderBill.grandtotal"] || 0),
        align: "right",
      },
      {
        title: "Payment Method",
        dataIndex: "PendingWorkOrderBill.payment_method",
        key: "payment",
      },
      {
        title: "PO Reference",
        dataIndex: "PendingWorkOrderBill.poreference",
        key: "po_ref",
      },
    ],
    categoryFilterDimension: {
      customer: "CBpartner.name",
      age: "PendingWorkOrderBill.agediff",
      product: "Product.name",
    },
    hasWarnings: false,
  },
};

// Detail Table Component
const DetailTable = ({ reportType, facility, customer, product, viewBy, category }) => {
  const config = REPORT_CONFIGS[reportType] || REPORT_CONFIGS.pending;

  // Build filters
  const filters = [];
  if (facility) {
    filters.push({ dimension: "AdOrg.facility", operator: "equals", values: [facility] });
  }
  if (customer) {
    filters.push({ dimension: "CBpartner.name", operator: "equals", values: [customer] });
  }
  if (product) {
    filters.push({ dimension: "Product.name", operator: "equals", values: [product] });
  }
  if (category && viewBy && config.categoryFilterDimension[viewBy]) {
    if (category === "N/A") {
      filters.push({
        dimension: config.categoryFilterDimension[viewBy],
        operator: "notSet",
      });
    } else {
      filters.push({
        dimension: config.categoryFilterDimension[viewBy],
        operator: "equals",
        values: [category],
      });
    }
  }

  const query = {
    ...config.query,
    filters,
    limit: 500,
  };

  const { resultSet, isLoading, error } = useCubeQuery(query);

  if (isLoading) return <Spin style={{ display: "block", margin: "40px auto" }} size="large" />;
  if (error) return <div style={{ color: "red", padding: 20 }}>Error loading data: {String(error)}</div>;
  if (!resultSet) return <div style={{ color: "#999", padding: 20 }}>Loading data...</div>;

  const rows = resultSet.tablePivot();
  const columns = config.columns(rows);

  return (
    <Table
      dataSource={rows}
      columns={columns}
      rowKey={(record, index) => index}
      pagination={{ pageSize: 50, showSizeChanger: true, pageSizeOptions: ["25", "50", "100", "200"] }}
      size="small"
      scroll={{ x: true }}
      onRow={(record) => {
        if (!config.hasWarnings) return {};
        const style = getRowWarningStyle(record, reportType);
        return { style };
      }}
      footer={() => <strong>{rows.length} records</strong>}
    />
  );
};

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }
  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }
  render() {
    if (this.state.hasError) {
      return <div style={{ color: "red", padding: 20 }}>Something went wrong: {String(this.state.error)}</div>;
    }
    return this.props.children;
  }
}

class NeedsActionDetail extends PureComponent {
  render() {
    const { history, location } = this.props;
    const params = new URLSearchParams(location.search);
    const reportType = params.get("report") || "pending";
    const facility = params.get("facility") || "";
    const customer = params.get("customer") || "";
    const product = params.get("product") || "";
    const viewBy = params.get("viewBy") || "";
    const category = params.get("category") || "";
    const config = REPORT_CONFIGS[reportType] || REPORT_CONFIGS.pending;

    const facilityName = facility ? `Facility ${facility}` : "All Facilities";

    return (
      <div style={{ padding: 16, background: "#f5f5f5", minHeight: "100vh" }}>
        {/* Header */}
        <div style={{ marginBottom: 16 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
            <div>
              <Button
                type="link"
                onClick={() => {
                  const backParams = new URLSearchParams();
                  backParams.set("report", reportType);
                  if (facility) backParams.set("facility", facility);
                  if (customer) backParams.set("customer", customer);
                  if (product) backParams.set("product", product);
                  history.push(`/needsaction/category?${backParams.toString()}`);
                }}
                style={{ padding: 0, marginBottom: 8 }}
              >
                &larr; Back to {config.title}
              </Button>
              <h2 style={{ margin: 0, fontSize: 20 }}>
                {config.title}
                <span style={{ color: "#666", fontWeight: "normal", fontSize: 16 }}>
                  {" "} - {facilityName}
                  {customer && ` - ${customer}`}
                  {product && ` - ${product}`}
                  {category && ` - ${category}`}
                </span>
              </h2>
            </div>
            {facility && (
              <Button
                type="primary"
                size="large"
                onClick={() => { window.location.hash = `/mwo?orgId=${facility}`; }}
                style={{ background: "#1a472a", borderColor: "#1a472a" }}
              >
                + Add WO
              </Button>
            )}
          </div>
        </div>

        {/* Warning Legend (for pending/delayed only) */}
        {config.hasWarnings && (
          <div style={{ marginBottom: 12, display: "flex", gap: 16, fontSize: 12 }}>
            <span><span style={{ display: "inline-block", width: 12, height: 12, background: "#fef9c3", border: "1px solid #ddd", marginRight: 4 }} /> Hazardous</span>
            <span><span style={{ display: "inline-block", width: 12, height: 12, background: "#fee2e2", border: "1px solid #ddd", marginRight: 4 }} /> Haz Warning</span>
            <span><span style={{ display: "inline-block", width: 12, height: 12, background: "#fde68a", border: "1px solid #ddd", marginRight: 4 }} /> Food Grade</span>
          </div>
        )}

        {/* Detail Table */}
        <div style={{ background: "#fff", borderRadius: 8, padding: 16 }}>
          <ErrorBoundary>
            <DetailTable reportType={reportType} facility={facility} customer={customer} product={product} viewBy={viewBy} category={category} />
          </ErrorBoundary>
        </div>
      </div>
    );
  }
}

export default withRouter(NeedsActionDetail);
